import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { API } from "aws-amplify";
import { createLLMModel, updateLLMModel } from "../../graphql/mutations";
import { listLLMModels } from "../../graphql/queries";
import { CreateLLMModelInput, UpdateLLMModelInput } from "../../API";
import {
  fetchOpenRouterCatalog,
  OpenRouterCatalogRecord,
} from "../../dataaccess/fetchOpenRouterCatalog";

interface ModelRecord {
  id: string;
  status: string;
  vendor: string;
  host: string;
  supported: boolean;
  name: string;
  description?: string | null;
  modelKey?: string | null;
  modelKeyLatest?: string | null;
  inputTokenPrice?: number | null;
  cachedTokenPrice?: number | null;
  outputTokenPrice?: number | null;  inputTokenBatchPrice?: number | null;
  outputTokenBatchPrice?: number | null;
  searchCost?: number | null;
  supportsSearch?: boolean | null;
  textInputSupport: boolean;
  imageInputSupport: boolean;
  audioInputSupport: boolean;
  textOutputSupport: boolean;
  imageOutputSupport: boolean;
  audioOutputSupport: boolean;
  thinkingSupport: boolean;
  contextWindowTokens?: number | null;
  maxResponseTokens?: number | null;  knowledgeCutoffDate?: string | null;
  lastCheckedDate?: string | null;
  supportedLanguages?: string | null;
  warningDate?: string | null;
  warningMessage?: string | null;
  deprecationDate?: string | null;
  deprecationMessage?: string | null;
  defaultServiceGroup?: string | null;
  defaultEnabled?: boolean | null;
  displayOrder?: number | null;
  recommendedUseCase?: string | null;
  performanceRating?: string | null;
}

// Interface for editing state - keeps price fields as strings to avoid input issues
interface EditingModelRecord extends Omit<ModelRecord, 'inputTokenPrice' | 'cachedTokenPrice' | 'outputTokenPrice' | 'inputTokenBatchPrice' | 'outputTokenBatchPrice' | 'searchCost'> {
  inputTokenPrice: string;
  cachedTokenPrice: string;
  outputTokenPrice: string;
  inputTokenBatchPrice: string;
  outputTokenBatchPrice: string;
  searchCost: string;
}

// Convert ModelRecord to EditingModelRecord for editing
const modelToEditingModel = (model: ModelRecord): EditingModelRecord => ({
  ...model,
  inputTokenPrice: model.inputTokenPrice?.toString() || "",
  cachedTokenPrice: model.cachedTokenPrice?.toString() || "",
  outputTokenPrice: model.outputTokenPrice?.toString() || "",
  inputTokenBatchPrice: model.inputTokenBatchPrice?.toString() || "",
  outputTokenBatchPrice: model.outputTokenBatchPrice?.toString() || "",
  searchCost: model.searchCost?.toString() || "",
});

// Convert string to number, handling empty strings and invalid values
const parseNumberOrUndefined = (value: string): number | undefined => {
  if (!value || value.trim() === "") return undefined;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
};

interface OpenRouterPriceComparison {
  openRouterModelId: string;
  openRouterInputTokenPrice: number | null;
  openRouterOutputTokenPrice: number | null;
  openRouterCachedTokenPrice: number | null;
  hasInputDifference: boolean;
  hasOutputDifference: boolean;
  hasCachedDifference: boolean;
  hasAnyDifference: boolean;
}

const OPENROUTER_PRICE_COMPARE_EPSILON = 1e-6;

const parsePipeDelimitedValues = (value?: string | null): string[] => {
  return String(value || "")
    .split("|")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

const nanoUsdPerTokenToUsdPerMillion = (value?: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed / 1000;
};

const formatPriceForPill = (value: number | null): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "-";
  }

  if (Math.abs(value) >= 100) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });
};

const pricesDiffer = (
  localPrice?: number | null,
  openRouterPrice?: number | null
): boolean => {
  const hasLocal = localPrice !== null && localPrice !== undefined;
  const hasOpenRouter = openRouterPrice !== null && openRouterPrice !== undefined;

  if (!hasLocal && !hasOpenRouter) {
    return false;
  }

  if (hasLocal !== hasOpenRouter) {
    return true;
  }

  return (
    Math.abs((localPrice as number) - (openRouterPrice as number)) >
    OPENROUTER_PRICE_COMPARE_EPSILON
  );
};

const buildOpenRouterLookup = (catalog: OpenRouterCatalogRecord[]) => {
  const lookup = new Map<string, OpenRouterCatalogRecord>();

  for (const record of catalog) {
    const idKey = String(record.model.id || "").trim().toLowerCase();
    if (idKey) {
      lookup.set(idKey, record);
    }

    const slugKey = String(record.model.canonicalSlug || "").trim().toLowerCase();
    if (slugKey) {
      lookup.set(slugKey, record);
    }
  }

  return lookup;
};

interface OpenRouterSearchEntry {
  record: OpenRouterCatalogRecord;
  modelIdLower: string;
  shortIdLower: string;
  canonicalSlugLower: string;
  modelNameLower: string;
  modelIdNormalized: string;
  shortIdNormalized: string;
  canonicalSlugNormalized: string;
  modelNameNormalized: string;
  providerIdLower: string;
  providerDisplayLower: string;
}

const normalizeForMatch = (value?: string | null): string => {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
};

const normalizeVendorForMatch = (value?: string | null): string => {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
};

const buildOpenRouterSearchIndex = (catalog: OpenRouterCatalogRecord[]): OpenRouterSearchEntry[] => {
  return catalog.map((record) => {
    const modelIdLower = String(record.model.id || "").trim().toLowerCase();
    const shortIdLower = modelIdLower.includes("/")
      ? modelIdLower.split("/").slice(1).join("/")
      : modelIdLower;
    const canonicalSlugLower = String(record.model.canonicalSlug || "")
      .trim()
      .toLowerCase();
    const modelNameLower = String(record.model.name || "").trim().toLowerCase();
    const providerIdLower = String(record.model.providerId || "")
      .trim()
      .toLowerCase();
    const providerDisplayLower = String(
      record.model.provider?.displayName || ""
    )
      .trim()
      .toLowerCase();

    return {
      record,
      modelIdLower,
      shortIdLower,
      canonicalSlugLower,
      modelNameLower,
      modelIdNormalized: normalizeForMatch(modelIdLower),
      shortIdNormalized: normalizeForMatch(shortIdLower),
      canonicalSlugNormalized: normalizeForMatch(canonicalSlugLower),
      modelNameNormalized: normalizeForMatch(modelNameLower),
      providerIdLower,
      providerDisplayLower,
    };
  });
};

const scoreOpenRouterCandidate = (
  query: string,
  queryNormalized: string,
  entry: OpenRouterSearchEntry,
  vendorKey: string
): number => {
  if (!query || !queryNormalized) {
    return 0;
  }

  let score = 0;

  if (query === entry.modelIdLower || query === entry.canonicalSlugLower) {
    score = Math.max(score, 200);
  } else if (query === entry.shortIdLower) {
    score = Math.max(score, 190);
  }

  if (
    queryNormalized &&
    (queryNormalized === entry.modelIdNormalized ||
      queryNormalized === entry.canonicalSlugNormalized)
  ) {
    score = Math.max(score, 180);
  } else if (queryNormalized === entry.shortIdNormalized) {
    score = Math.max(score, 170);
  } else if (queryNormalized === entry.modelNameNormalized) {
    score = Math.max(score, 165);
  }

  if (queryNormalized.length >= 5) {
    if (entry.shortIdNormalized.includes(queryNormalized)) {
      score = Math.max(score, 140);
    }
    if (entry.canonicalSlugNormalized.includes(queryNormalized)) {
      score = Math.max(score, 135);
    }
    if (entry.modelNameNormalized.includes(queryNormalized)) {
      score = Math.max(score, 130);
    }
  }

  if (vendorKey) {
    const providerKey = normalizeVendorForMatch(entry.providerIdLower);
    const providerDisplayKey = normalizeVendorForMatch(entry.providerDisplayLower);
    if (providerKey && vendorKey === providerKey) {
      score += 25;
    } else if (providerDisplayKey && vendorKey === providerDisplayKey) {
      score += 20;
    }
  }

  if (entry.record.model.isDeprecated) {
    score -= 10;
  }

  return score;
};

const findBestOpenRouterCatalogMatch = (
  model: ModelRecord,
  lookup: Map<string, OpenRouterCatalogRecord>,
  searchIndex: OpenRouterSearchEntry[]
): OpenRouterCatalogRecord | null => {
  const rawCandidates = [
    ...parsePipeDelimitedValues(model.modelKeyLatest),
    ...parsePipeDelimitedValues(model.modelKey),
    model.name || "",
  ]
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  if (rawCandidates.length === 0) {
    return null;
  }

  for (const candidate of rawCandidates) {
    const exactMatch = lookup.get(candidate.toLowerCase());
    if (exactMatch) {
      return exactMatch;
    }
  }

  const vendorKey = normalizeVendorForMatch(model.vendor);
  let bestEntry: OpenRouterSearchEntry | null = null;
  let bestScore = 0;

  for (const candidate of rawCandidates) {
    const candidateLower = candidate.toLowerCase();
    const candidateNormalized = normalizeForMatch(candidate);
    if (!candidateNormalized) {
      continue;
    }

    for (const entry of searchIndex) {
      const score = scoreOpenRouterCandidate(
        candidateLower,
        candidateNormalized,
        entry,
        vendorKey
      );

      if (score > bestScore) {
        bestScore = score;
        bestEntry = entry;
      }
    }
  }

  return bestScore >= 130 ? bestEntry?.record || null : null;
};

export default function AdminModels() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [models, setModels] = useState<ModelRecord[]>([]);
  const [selectedModel, setSelectedModel] = useState<EditingModelRecord | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [filterVendor, setFilterVendor] = useState("");
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStatusFilters, setExportStatusFilters] = useState<Record<string, boolean>>({});
  const [comparingOpenRouterPrices, setComparingOpenRouterPrices] = useState(false);
  const [openRouterPriceComparisons, setOpenRouterPriceComparisons] = useState<
    Record<string, OpenRouterPriceComparison>
  >({});
  const [openRouterNoMatchModelIds, setOpenRouterNoMatchModelIds] = useState<
    Record<string, boolean>
  >({});
  const [showPriceMismatchesOnly, setShowPriceMismatchesOnly] = useState(false);

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    // Check for URL parameters to auto-open model for editing
    const urlParams = new URLSearchParams(window.location.search);
    const editModelId = urlParams.get('edit');
    
    if (editModelId && models.length > 0) {
      const modelToEdit = models.find(model => model.id === editModelId);
      if (modelToEdit) {
        handleEditClick(modelToEdit);
        // Clear the URL parameter after opening the dialog
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [models]); // Depend on models so this runs after models are loaded
  // Initialize export status filters when models change
  useEffect(() => {
    if (models.length > 0) {
      const uniqueStatuses = Array.from(new Set(models.map(model => model.status)));
      const initialFilters: Record<string, boolean> = {};
      uniqueStatuses.forEach(status => {
        initialFilters[status] = true; // Default to all selected
      });
      setExportStatusFilters(initialFilters);
    }
  }, [models]);

  const fetchModels = async (): Promise<ModelRecord[]> => {
    setLoading(true);
    try {
      const result = (await API.graphql({ query: listLLMModels })) as {
        data: {
          listLLMModels: {
            items: ModelRecord[];
          };
        };
      };
      const items = result.data.listLLMModels.items || [];
      setModels(items);
      return items;
    } catch (err) {
      setError("Error fetching models: " + String(err));
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (model: ModelRecord) => {
    setSelectedModel(modelToEditingModel(model));
    setIsCreatingNew(false);
    setOpenEditDialog(true);
  };

  const handleDuplicateClick = (model: ModelRecord) => {
    const duplicatedModel: ModelRecord = {
      ...model,
      id: "",
      modelKey: model.modelKey || "",
      modelKeyLatest: model.modelKeyLatest || "",
    };

    setSelectedModel(modelToEditingModel(duplicatedModel));
    setIsCreatingNew(true);
    setOpenEditDialog(true);
  };

  const handleAddClick = () => {
    // Create a new model with default values
    const newModel: ModelRecord = {
      id: '', // Will be generated by the backend
      status: 'active',
      vendor: '',
      host: '',
      supported: true,
      name: '',
      description: '',
      modelKey: '',
      modelKeyLatest: '',
      inputTokenPrice: 0,
      cachedTokenPrice: 0,
      outputTokenPrice: 0,
      inputTokenBatchPrice: 0,
      outputTokenBatchPrice: 0,      searchCost: 0,
      supportsSearch: false,
      textInputSupport: true,
      imageInputSupport: false,
      audioInputSupport: false,
      textOutputSupport: true,
      imageOutputSupport: false,
      audioOutputSupport: false,
      thinkingSupport: false,
      contextWindowTokens: 0,
      maxResponseTokens: 0,      knowledgeCutoffDate: '',
      lastCheckedDate: '',
      supportedLanguages: '',
      warningDate: '',
      warningMessage: '',
      deprecationDate: '',
      deprecationMessage: '',
      defaultServiceGroup: 'Standard',
      defaultEnabled: true,
      displayOrder: 0,
      recommendedUseCase: '',
      performanceRating: 'Balanced',
    };
    setSelectedModel(modelToEditingModel(newModel));
    setIsCreatingNew(true);
    setOpenEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!selectedModel) {
      console.log("No selected model");
      return;
    }

    console.log("Saving model:", selectedModel);
    console.log("Is creating new:", isCreatingNew);

    try {      if (isCreatingNew) {
        // Create new model
        const createLLMModelInput: CreateLLMModelInput = {
          status: selectedModel.status,
          vendor: selectedModel.vendor,
          host: selectedModel.host,
          supported: selectedModel.supported,
          name: selectedModel.name,
          description: selectedModel.description || undefined,
          modelKey: selectedModel.modelKey || undefined,
          modelKeyLatest: selectedModel.modelKeyLatest || undefined,
          inputTokenPrice: parseNumberOrUndefined(selectedModel.inputTokenPrice),
          cachedTokenPrice: parseNumberOrUndefined(selectedModel.cachedTokenPrice),
          outputTokenPrice: parseNumberOrUndefined(selectedModel.outputTokenPrice),
          inputTokenBatchPrice: parseNumberOrUndefined(selectedModel.inputTokenBatchPrice),
          outputTokenBatchPrice: parseNumberOrUndefined(selectedModel.outputTokenBatchPrice),
          searchCost: parseNumberOrUndefined(selectedModel.searchCost),
          supportsSearch: selectedModel.supportsSearch,
          textInputSupport: selectedModel.textInputSupport,
          imageInputSupport: selectedModel.imageInputSupport,
          audioInputSupport: selectedModel.audioInputSupport,
          textOutputSupport: selectedModel.textOutputSupport,
          imageOutputSupport: selectedModel.imageOutputSupport,
          audioOutputSupport: selectedModel.audioOutputSupport,          thinkingSupport: selectedModel.thinkingSupport,
          contextWindowTokens: selectedModel.contextWindowTokens || undefined,
          maxResponseTokens: selectedModel.maxResponseTokens || undefined,          knowledgeCutoffDate: selectedModel.knowledgeCutoffDate || undefined,
          lastCheckedDate: selectedModel.lastCheckedDate || undefined,
          supportedLanguages: selectedModel.supportedLanguages || undefined,
          warningDate: selectedModel.warningDate || undefined,
          warningMessage: selectedModel.warningMessage || undefined,
          deprecationDate: selectedModel.deprecationDate || undefined,
          deprecationMessage: selectedModel.deprecationMessage || undefined,
          defaultServiceGroup: selectedModel.defaultServiceGroup || undefined,
          defaultEnabled: selectedModel.defaultEnabled !== null ? selectedModel.defaultEnabled : undefined,
          displayOrder: selectedModel.displayOrder || undefined,
          recommendedUseCase: selectedModel.recommendedUseCase || undefined,
          performanceRating: selectedModel.performanceRating || undefined,
        };

        console.log("Create input:", createLLMModelInput);

        try {
          const response = await API.graphql({
            query: createLLMModel,
            variables: { input: createLLMModelInput },
          });
          console.log("Create successful:", response);
          setSuccess("Model created successfully");
          setOpenEditDialog(false);
          setIsCreatingNew(false);
          await fetchModels();
        } catch (graphqlError) {
          console.error("GraphQL Create Error:", graphqlError);
          
          if (graphqlError && typeof graphqlError === 'object' && 'errors' in graphqlError) {
            const errorMessages = (graphqlError as any).errors?.map((e: any) => e.message).join(', ');
            setError("GraphQL Error: " + errorMessages);
          } else {
            setError("Error creating model: " + String(graphqlError));
          }
          return;
        }
      } else {
        // Update existing model
        console.log("Creating update input:", selectedModel);
        console.log("Selected model status:", selectedModel.status);        const updateLLMModelInput: UpdateLLMModelInput = {
          id: selectedModel.id,
          status: selectedModel.status,
          vendor: selectedModel.vendor,
          host: selectedModel.host,
          supported: selectedModel.supported,
          name: selectedModel.name,
          description: selectedModel.description ?? null,
          modelKey: selectedModel.modelKey ?? undefined,
          modelKeyLatest: selectedModel.modelKeyLatest ?? undefined,
          inputTokenPrice: parseNumberOrUndefined(selectedModel.inputTokenPrice),
          cachedTokenPrice: parseNumberOrUndefined(selectedModel.cachedTokenPrice),
          outputTokenPrice: parseNumberOrUndefined(selectedModel.outputTokenPrice),
          inputTokenBatchPrice: parseNumberOrUndefined(selectedModel.inputTokenBatchPrice),          outputTokenBatchPrice: parseNumberOrUndefined(selectedModel.outputTokenBatchPrice),
          searchCost: parseNumberOrUndefined(selectedModel.searchCost),
          supportsSearch: selectedModel.supportsSearch,
          textInputSupport: selectedModel.textInputSupport,
          imageInputSupport: selectedModel.imageInputSupport,
          audioInputSupport: selectedModel.audioInputSupport,
          textOutputSupport: selectedModel.textOutputSupport,
          imageOutputSupport: selectedModel.imageOutputSupport,
          audioOutputSupport: selectedModel.audioOutputSupport,          thinkingSupport: selectedModel.thinkingSupport,
          contextWindowTokens: selectedModel.contextWindowTokens
            ? selectedModel.contextWindowTokens
            : undefined,
          maxResponseTokens: selectedModel.maxResponseTokens
            ? selectedModel.maxResponseTokens
            : undefined,          knowledgeCutoffDate: selectedModel.knowledgeCutoffDate
            ? selectedModel.knowledgeCutoffDate
            : undefined,
          lastCheckedDate: selectedModel.lastCheckedDate
            ? selectedModel.lastCheckedDate
            : undefined,
          supportedLanguages: selectedModel.supportedLanguages
            ? selectedModel.supportedLanguages
            : undefined,
          warningDate: selectedModel.warningDate
            ? selectedModel.warningDate
            : undefined,
          warningMessage: selectedModel.warningMessage
            ? selectedModel.warningMessage
            : undefined,
          deprecationDate: selectedModel.deprecationDate
            ? selectedModel.deprecationDate
            : undefined,
          deprecationMessage: selectedModel.deprecationMessage
            ? selectedModel.deprecationMessage
            : undefined,
          defaultServiceGroup: selectedModel.defaultServiceGroup
            ? selectedModel.defaultServiceGroup
            : undefined,defaultEnabled: selectedModel.defaultEnabled !== null
            ? selectedModel.defaultEnabled
            : undefined,
          displayOrder: selectedModel.displayOrder
            ? selectedModel.displayOrder
            : undefined,
          recommendedUseCase: selectedModel.recommendedUseCase
            ? selectedModel.recommendedUseCase
            : undefined,
          performanceRating: selectedModel.performanceRating
            ? selectedModel.performanceRating
            : undefined,
        };console.log("Update input:", updateLLMModelInput);
        console.log("Status being sent:", updateLLMModelInput.status);
        console.log("Calling API.graphql with mutation:", updateLLMModel);

        try {
          const response = await API.graphql({
            query: updateLLMModel,
            variables: { input: updateLLMModelInput },
          });
          console.log("Raw API Response:", response);

          if ("errors" in response) {
            throw new Error(JSON.stringify(response.errors));
          }

          console.log("Update successful:", response);
          setSuccess("Model updated successfully");
          setOpenEditDialog(false);
          await fetchModels();      } catch (graphqlError) {
          console.error("GraphQL Error:", graphqlError);
          
          // Extract more detailed error information
          if (graphqlError && typeof graphqlError === 'object' && 'errors' in graphqlError) {
            const errorMessages = (graphqlError as any).errors?.map((e: any) => e.message).join(', ');
            setError("GraphQL Error: " + errorMessages);
          } else {
            setError("Error updating model: " + String(graphqlError));
          }
          return; // Don't throw, just show the error
        }
      }
    } catch (err) {
      console.error("Save Error:", err);      setError("Error saving model: " + String(err));
    }
  };
  const handleExport = () => {
    setExportDialogOpen(true);
  };

  const handleExportConfirm = () => {
    try {
      // Filter models based on selected statuses
      const selectedStatuses = Object.keys(exportStatusFilters).filter(status => exportStatusFilters[status]);
      const filteredModels = models.filter(model => selectedStatuses.includes(model.status));

      // Create export data with timestamp
      const exportData = {
        exportedAt: new Date().toISOString(),
        exportedBy: "AdminModels",
        version: "1.0",
        statusFilters: selectedStatuses,
        models: filteredModels
      };

      // Convert to JSON string with pretty formatting
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob and download link
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `llm-models-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      URL.revokeObjectURL(url);
      
      setSuccess(`Exported ${filteredModels.length} models successfully (filtered by status: ${selectedStatuses.join(', ')})`);
      setExportDialogOpen(false);
    } catch (err) {
      setError("Error exporting models: " + String(err));    }
  };

  const handleStatusFilterChange = (status: string, checked: boolean) => {
    setExportStatusFilters(prev => ({
      ...prev,
      [status]: checked
    }));
  };

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        setImportData(content);
        setImportDialogOpen(true);
      } catch (err) {
        setError("Error reading file: " + String(err));
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  const validateImportData = (data: any): ModelRecord[] => {
    if (!data || typeof data !== 'object') {
      throw new Error("Invalid JSON format");
    }

    let modelsToImport: any[];
    
    // Handle both direct array format and wrapped export format
    if (Array.isArray(data)) {
      modelsToImport = data;
    } else if (data.models && Array.isArray(data.models)) {
      modelsToImport = data.models;
    } else {
      throw new Error("Expected an array of models or export format with 'models' property");
    }

    // Validate each model has required fields
    const requiredFields = ['vendor', 'name', 'status'];
    const validatedModels: ModelRecord[] = [];

    for (let i = 0; i < modelsToImport.length; i++) {
      const model = modelsToImport[i];
      
      // Check required fields
      for (const field of requiredFields) {
        if (!model[field]) {
          throw new Error(`Model at index ${i} is missing required field: ${field}`);
        }
      }

      // Create validated model record with defaults for missing fields
      const validatedModel: ModelRecord = {
        id: model.id || '', // Will be generated by backend if empty
        status: model.status,
        vendor: model.vendor,
        host: model.host || '',
        supported: model.supported !== undefined ? model.supported : true,
        name: model.name,
        description: model.description || null,
        modelKey: model.modelKey || null,
        modelKeyLatest: model.modelKeyLatest || null,
        inputTokenPrice: model.inputTokenPrice || null,
        cachedTokenPrice: model.cachedTokenPrice || null,
        outputTokenPrice: model.outputTokenPrice || null,
        inputTokenBatchPrice: model.inputTokenBatchPrice || null,
        outputTokenBatchPrice: model.outputTokenBatchPrice || null,        searchCost: model.searchCost || null,
        supportsSearch: model.supportsSearch !== undefined ? model.supportsSearch : false,
        textInputSupport: model.textInputSupport !== undefined ? model.textInputSupport : true,
        imageInputSupport: model.imageInputSupport !== undefined ? model.imageInputSupport : false,
        audioInputSupport: model.audioInputSupport !== undefined ? model.audioInputSupport : false,
        textOutputSupport: model.textOutputSupport !== undefined ? model.textOutputSupport : true,
        imageOutputSupport: model.imageOutputSupport !== undefined ? model.imageOutputSupport : false,
        audioOutputSupport: model.audioOutputSupport !== undefined ? model.audioOutputSupport : false,
        thinkingSupport: model.thinkingSupport !== undefined ? model.thinkingSupport : false,
        contextWindowTokens: model.contextWindowTokens || null,
        maxResponseTokens: model.maxResponseTokens || null,        knowledgeCutoffDate: model.knowledgeCutoffDate || null,
        lastCheckedDate: model.lastCheckedDate || null,
        supportedLanguages: model.supportedLanguages || null,
        warningDate: model.warningDate || null,
        warningMessage: model.warningMessage || null,
        deprecationDate: model.deprecationDate || null,
        deprecationMessage: model.deprecationMessage || null,
        defaultServiceGroup: model.defaultServiceGroup || null,
        defaultEnabled: model.defaultEnabled || null,
        displayOrder: model.displayOrder || null,
        recommendedUseCase: model.recommendedUseCase || null,
        performanceRating: model.performanceRating || null,
      };

      validatedModels.push(validatedModel);
    }

    return validatedModels;
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      setError("No import data provided");
      return;
    }

    setImporting(true);
    try {
      // Parse JSON
      const parsedData = JSON.parse(importData);
      
      // Validate data structure
      const modelsToImport = validateImportData(parsedData);
      
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Import each model
      for (let i = 0; i < modelsToImport.length; i++) {
        const model = modelsToImport[i];
        
        try {
          const createInput: CreateLLMModelInput = {
            status: model.status,
            vendor: model.vendor,
            host: model.host,
            supported: model.supported,
            name: model.name,
            description: model.description || undefined,
            modelKey: model.modelKey || undefined,
            modelKeyLatest: model.modelKeyLatest || undefined,
            inputTokenPrice: model.inputTokenPrice || undefined,
            cachedTokenPrice: model.cachedTokenPrice || undefined,
            outputTokenPrice: model.outputTokenPrice || undefined,
            inputTokenBatchPrice: model.inputTokenBatchPrice || undefined,
            outputTokenBatchPrice: model.outputTokenBatchPrice || undefined,            searchCost: model.searchCost || undefined,
            supportsSearch: model.supportsSearch,
            textInputSupport: model.textInputSupport,
            imageInputSupport: model.imageInputSupport,
            audioInputSupport: model.audioInputSupport,
            textOutputSupport: model.textOutputSupport,
            imageOutputSupport: model.imageOutputSupport,
            audioOutputSupport: model.audioOutputSupport,
            thinkingSupport: model.thinkingSupport,
            contextWindowTokens: model.contextWindowTokens || undefined,
            maxResponseTokens: model.maxResponseTokens || undefined,            knowledgeCutoffDate: model.knowledgeCutoffDate || undefined,
            lastCheckedDate: model.lastCheckedDate || undefined,
            supportedLanguages: model.supportedLanguages || undefined,
            warningDate: model.warningDate || undefined,
            warningMessage: model.warningMessage || undefined,
            deprecationDate: model.deprecationDate || undefined,
            deprecationMessage: model.deprecationMessage || undefined,
            defaultServiceGroup: model.defaultServiceGroup || undefined,
            defaultEnabled: model.defaultEnabled !== null ? model.defaultEnabled : undefined,
            displayOrder: model.displayOrder || undefined,
            recommendedUseCase: model.recommendedUseCase || undefined,
            performanceRating: model.performanceRating || undefined,
          };

          await API.graphql({
            query: createLLMModel,
            variables: { input: createInput },
          });
          
          successCount++;
        } catch (err) {
          errorCount++;
          const errorMsg = `Model "${model.name}" (${model.vendor}): ${String(err)}`;
          errors.push(errorMsg);
          console.error(`Import error for model ${i}:`, err);
        }
      }

      // Show results
      if (successCount > 0) {
        setSuccess(`Successfully imported ${successCount} models${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
      }
      
      if (errorCount > 0) {
        setError(`Failed to import ${errorCount} models. First few errors: ${errors.slice(0, 3).join('; ')}`);
      }

      // Refresh models list
      await fetchModels();
      
      // Close dialog
      setImportDialogOpen(false);
      setImportData("");
      
    } catch (err) {
      setError("Error importing models: " + String(err));
    }
    
    setImporting(false);
  };

  const compareOpenRouterPrices = async (modelsToCompare: ModelRecord[] = models) => {
    const catalog = await fetchOpenRouterCatalog();
    const openRouterLookup = buildOpenRouterLookup(catalog);
    const openRouterSearchIndex = buildOpenRouterSearchIndex(catalog);
    const comparisons: Record<string, OpenRouterPriceComparison> = {};
    const noMatchModelIds: Record<string, boolean> = {};

    for (const model of modelsToCompare) {
      const match = findBestOpenRouterCatalogMatch(
        model,
        openRouterLookup,
        openRouterSearchIndex
      );

      if (!match) {
        noMatchModelIds[model.id] = true;
        continue;
      }

      const openRouterInputTokenPrice = nanoUsdPerTokenToUsdPerMillion(
        match.currentPricing?.promptTokenUsdNano || null
      );
      const openRouterOutputTokenPrice = nanoUsdPerTokenToUsdPerMillion(
        match.currentPricing?.completionTokenUsdNano || null
      );
      const openRouterCachedTokenPrice = nanoUsdPerTokenToUsdPerMillion(
        match.currentPricing?.cacheReadTokenUsdNano || null
      );

      const hasInputDifference = pricesDiffer(
        model.inputTokenPrice,
        openRouterInputTokenPrice
      );
      const hasOutputDifference = pricesDiffer(
        model.outputTokenPrice,
        openRouterOutputTokenPrice
      );
      const hasCachedDifference = pricesDiffer(
        model.cachedTokenPrice,
        openRouterCachedTokenPrice
      );

      comparisons[model.id] = {
        openRouterModelId: match.model.id,
        openRouterInputTokenPrice,
        openRouterOutputTokenPrice,
        openRouterCachedTokenPrice,
        hasInputDifference,
        hasOutputDifference,
        hasCachedDifference,
        hasAnyDifference:
          hasInputDifference || hasOutputDifference || hasCachedDifference,
      };
    }

    setOpenRouterPriceComparisons(comparisons);
    setOpenRouterNoMatchModelIds(noMatchModelIds);

    const mismatchedModelIds = Object.entries(comparisons)
      .filter(([, comparison]) => comparison.hasAnyDifference)
      .map(([modelId]) => modelId);

    return {
      comparisons,
      comparedCount: Object.keys(comparisons).length,
      mismatchedModelIds,
    };
  };

  const handleCompareOpenRouterPrices = async () => {
    setComparingOpenRouterPrices(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await compareOpenRouterPrices();
      setSuccess(
        `Compared ${result.comparedCount} OpenRouter-linked models. Found ${result.mismatchedModelIds.length} with price differences.`
      );
    } catch (err) {
      setError("Error comparing OpenRouter prices: " + String(err));
    } finally {
      setComparingOpenRouterPrices(false);
    }
  };

  const mismatchCount =
    Object.values(openRouterPriceComparisons).filter(
      (comparison) => comparison.hasAnyDifference
    ).length + Object.keys(openRouterNoMatchModelIds).length;

  const comparedModelCount = Object.keys(openRouterPriceComparisons).length;

  const filteredModels = models
    .filter((model) =>
      model.vendor.toLowerCase().includes(filterVendor.toLowerCase())
    )
    .filter((model) => {
      if (!showPriceMismatchesOnly) {
        return true;
      }
      return (
        openRouterPriceComparisons[model.id]?.hasAnyDifference === true ||
        openRouterNoMatchModelIds[model.id] === true
      );
    });
  
  const columns: GridColDef[] = [
    { field: "vendor", headerName: "Vendor", width: 100 },
    { field: "host", headerName: "Host", width: 100 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "name", headerName: "Name", width: 200 },
    {
      field: "supported",
      headerName: "Supported",
      width: 100,
      type: "boolean",
    },
    {
      field: "inputTokenPrice",
      headerName: "Input Price",
      width: 130,
      type: "number",
      cellClassName: (params) =>
        openRouterPriceComparisons[params.row.id]?.hasInputDifference
          ? "openrouter-price-diff-cell"
          : "",
    },
    {
      field: "outputTokenPrice",
      headerName: "Output Price",
      width: 130,
      type: "number",
      cellClassName: (params) =>
        openRouterPriceComparisons[params.row.id]?.hasOutputDifference
          ? "openrouter-price-diff-cell"
          : "",
    },
    {
      field: "cachedTokenPrice",
      headerName: "Cached Price",
      width: 130,
      type: "number",
      cellClassName: (params) =>
        openRouterPriceComparisons[params.row.id]?.hasCachedDifference
          ? "openrouter-price-diff-cell"
          : "",
    },
    {
      field: "openRouterPricingStatus",
      headerName: "OpenRouter Pricing",
      width: 360,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const comparison = openRouterPriceComparisons[params.row.id];
        const hasNoMatch = openRouterNoMatchModelIds[params.row.id] === true;
        if (!comparison && hasNoMatch) {
          return (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label="No Match"
            />
          );
        }
        if (!comparison) return <Typography variant="caption">-</Typography>;

        const diffLabels = [];
        if (comparison.hasInputDifference) diffLabels.push("Input");
        if (comparison.hasOutputDifference) diffLabels.push("Output");
        if (comparison.hasCachedDifference) diffLabels.push("Cached");

        if (comparison.hasAnyDifference) {
          const openRouterPriceText = `OR I:${formatPriceForPill(
            comparison.openRouterInputTokenPrice
          )} O:${formatPriceForPill(
            comparison.openRouterOutputTokenPrice
          )} C:${formatPriceForPill(comparison.openRouterCachedTokenPrice)}`;

          return (
            <Chip
              size="small"
              color="warning"
              label={`Diff ${diffLabels.join(", ")} | ${openRouterPriceText}`}
              title={openRouterPriceText}
            />
          );
        }

        return <Chip size="small" color="success" label="Matched" />;
      },
    },

    {
      field: "imageInputSupport",
      headerName: "Image In",
      width: 100,
      type: "boolean",
    },
    {
      field: "audioInputSupport",
      headerName: "Audio In",
      width: 100,
      type: "boolean",
    },
    {
      field: "textOutputSupport",
      headerName: "Text Out",
      width: 100,
      type: "boolean",
    },
    {
      field: "imageOutputSupport",
      headerName: "Image Out",
      width: 100,
      type: "boolean",
    },
    {
      field: "audioOutputSupport",
      headerName: "Audio Out",
      width: 100,
      type: "boolean",
    },    {
      field: "thinkingSupport",
      headerName: "Reasoning",
      width: 100,
      type: "boolean",
    },
    {
      field: "supportsSearch",
      headerName: "Search",
      width: 100,
      type: "boolean",
    },
    {
      field: "contextWindowTokens",
      headerName: "Context Tokens",
      width: 130,
      type: "number",
    },
    {
      field: "maxResponseTokens",
      headerName: "Max Tokens",
      width: 130,
      type: "number",
    },    {
      field: "knowledgeCutoffDate",
      headerName: "Knowledge Cutoff",
      width: 150,
    },
    {
      field: "warningDate",
      headerName: "Warning Date",
      width: 130,
    },
    {
      field: "warningMessage",
      headerName: "Warning Message",
      width: 200,
    },
    {
      field: "deprecationDate",
      headerName: "Deprecation Date",
      width: 140,
    },
    {
      field: "deprecationMessage",
      headerName: "Deprecation Message",
      width: 200,
    },
    {
      field: "defaultServiceGroup",
      headerName: "Default Group",
      width: 120,
    },
    {
      field: "defaultEnabled",
      headerName: "Default Enabled",
      width: 120,
      type: "boolean",
    },
    {
      field: "displayOrder",
      headerName: "Display Order",
      width: 110,
      type: "number",
    },
    {
      field: "performanceRating",
      headerName: "Performance",
      width: 110,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 210,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleEditClick(params.row as ModelRecord)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={() => handleDuplicateClick(params.row as ModelRecord)}
          >
            Duplicate
          </Button>
        </Box>
      ),
    },  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Models Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddClick}
        >
          Add New Model
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleExport}
          disabled={models.length === 0}
        >
          Export Models ({models.length})
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          component="label"
        >
          Import Models
          <input
            type="file"
            hidden
            accept=".json"
            onChange={handleImportFile}
          />
        </Button>
        <Button
          variant="outlined"
          color="info"
          onClick={handleCompareOpenRouterPrices}
          disabled={models.length === 0 || comparingOpenRouterPrices}
        >
          {comparingOpenRouterPrices ? (
            <CircularProgress size={20} />
          ) : (
            "Compare OpenRouter Prices"
          )}
        </Button>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showPriceMismatchesOnly}
              onChange={(event) => setShowPriceMismatchesOnly(event.target.checked)}
              disabled={comparedModelCount === 0}
            />
          }
          label={`Only Mismatches (${mismatchCount})`}
        />
        <TextField
          label="Filter by Vendor"
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          size="small"
        />
      </Box>

      <Box sx={{ height: "100%", width: "100%" }}>
        <DataGrid
          rows={filteredModels}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          columns={columns}
          getRowHeight={() => "auto"}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            sorting: {
              sortModel: [{ field: "outputTokenPrice", sort: "desc" }],
            },
            columns: {
              columnVisibilityModel: {
                id: false,
                vendor: true,
                host: false,
                supported: true,
                status: true,
                name: true,
                description: false,
                inputTokenPrice: true,
                outputTokenPrice: true,
                contextWindowTokens: true,
                maxResponseTokens: true,
                textInputSupport: false,
                imageInputSupport: false,
                audioInputSupport: false,
                textOutputSupport: false,
                imageOutputSupport: false,
                audioOutputSupport: false,
                thinkingSupport: false,                knowledgeCutoffDate: false,
                lastCheckedDate: true,
                supportedLanguages: false,
                warningDate: false,
                warningMessage: false,
                deprecationDate: false,
                deprecationMessage: false,
                defaultServiceGroup: true,
                defaultEnabled: true,
                displayOrder: false,
                performanceRating: true,
                recommendedUseCase: false,
              },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          getRowClassName={(params) =>
            openRouterPriceComparisons[params.row.id]?.hasAnyDifference
              ? "openrouter-price-diff-row"
              : ""
          }
          //disableSelectionOnClick
          loading={loading}
          sx={{
            "& .openrouter-price-diff-row": {
              bgcolor: "rgba(237, 108, 2, 0.08)",
            },
            "& .openrouter-price-diff-cell": {
              bgcolor: "rgba(237, 108, 2, 0.18)",
              fontWeight: 600,
            },
            "& .MuiDataGrid-cell": {
              py: 1,
            },
          }}
        />
      </Box>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isCreatingNew ? 'Add New Model' : 'Edit Model'}</DialogTitle>
        <DialogContent>
          {selectedModel && (
            <Box
              sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {/* Basic Info */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Vendor"
                  value={selectedModel.vendor}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      vendor: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Host"
                  value={selectedModel.host}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      host: e.target.value,
                    })
                  }
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={selectedModel.status}
                    onChange={(e) =>
                      setSelectedModel({
                        ...selectedModel,
                        status: e.target.value,
                      })
                    }                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="research">Research</MenuItem>
                    <MenuItem value="beingDeprecated">
                      Being Deprecated
                    </MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                    <MenuItem value="deleted">Deleted</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Name"
                  value={selectedModel.name}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      name: e.target.value,
                    })
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.supported}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          supported: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Supported"
                />
                <TextField
                  size="small"
                  label="Model Key"
                  value={selectedModel.modelKey || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      modelKey: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Latest Model Key"
                  value={selectedModel.modelKeyLatest || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      modelKeyLatest: e.target.value,
                    })
                  }
                />
              </Box>

              {/* Pricing */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                }}
              >                <TextField
                  size="small"
                  label="Input Token Price"
                  type="number"
                  value={selectedModel.inputTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      inputTokenPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Output Token Price"
                  type="number"
                  value={selectedModel.outputTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      outputTokenPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Cached Token Price"
                  type="number"
                  value={selectedModel.cachedTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      cachedTokenPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Input Batch Price"
                  type="number"
                  value={selectedModel.inputTokenBatchPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      inputTokenBatchPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Output Batch Price"
                  type="number"
                  value={selectedModel.outputTokenBatchPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      outputTokenBatchPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Search Cost"
                  type="number"
                  value={selectedModel.searchCost || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      searchCost: e.target.value,
                    })
                  }
                />
              </Box>

              {/* Support Flags */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.textInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          textInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Text Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.imageInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          imageInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Image Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.audioInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          audioInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Audio Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.textOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          textOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Text Output"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.imageOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          imageOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Image Output"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.audioOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          audioOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Audio Output"
                />                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.thinkingSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          thinkingSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Reasoning"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.supportsSearch || false}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          supportsSearch: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Supports Search"
                />
              </Box>

              {/* Other Fields */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                }}
              >                <TextField
                  size="small"
                  label="Context Window Tokens"
                  type="number"
                  value={selectedModel.contextWindowTokens || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      contextWindowTokens: parseInt(e.target.value) || undefined,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Max Response Tokens"
                  type="number"
                  value={selectedModel.maxResponseTokens || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      maxResponseTokens: parseInt(e.target.value) || undefined,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Knowledge Cutoff Date"
                  value={selectedModel.knowledgeCutoffDate || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      knowledgeCutoffDate: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Last Checked Date"
                  value={selectedModel.lastCheckedDate || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      lastCheckedDate: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Description"
                  multiline
                  rows={4}
                  value={selectedModel.description || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      description: e.target.value,
                    })
                  }
                  sx={{ gridColumn: "span 2" }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 2,
                }}
              >                <TextField
                  size="small"
                  label="Supported Languages"
                  multiline
                  rows={2}
                  value={selectedModel.supportedLanguages || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      supportedLanguages: e.target.value,
                    })
                  }
                />
              </Box>

              {/* New Service Management Fields */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Service Management Settings
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                }}
              >
                <FormControl size="small" fullWidth>
                  <InputLabel>Default Service Group</InputLabel>
                  <Select
                    label="Default Service Group"
                    value={selectedModel.defaultServiceGroup || ""}
                    onChange={(e) =>
                      setSelectedModel({
                        ...selectedModel,
                        defaultServiceGroup: e.target.value,
                      })
                    }                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Super Small">Super Small</MenuItem>
                    <MenuItem value="Standard">Standard</MenuItem>
                    <MenuItem value="Premium">Premium</MenuItem>
                    <MenuItem value="Reasoning">Reasoning</MenuItem>
                    <MenuItem value="Super Premium">Super Premium</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.defaultEnabled || false}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          defaultEnabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Default Enabled"
                />
                <TextField
                  size="small"
                  label="Display Order"
                  type="number"
                  value={selectedModel.displayOrder || ""}                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      displayOrder: parseInt(e.target.value) || undefined,
                    })
                  }
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Performance Rating</InputLabel>
                  <Select
                    label="Performance Rating"
                    value={selectedModel.performanceRating || ""}
                    onChange={(e) =>
                      setSelectedModel({
                        ...selectedModel,
                        performanceRating: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Fast">Fast</MenuItem>
                    <MenuItem value="Balanced">Balanced</MenuItem>
                    <MenuItem value="Powerful">Powerful</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Recommended Use Case"
                  multiline
                  rows={2}
                  value={selectedModel.recommendedUseCase || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      recommendedUseCase: e.target.value,
                    })
                  }
                  helperText="Describe when users should choose this model"
                />
              </Box>
            </Box>
          )}
        </DialogContent>        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setIsCreatingNew(false);
          }}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            {isCreatingNew ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export LLM Models</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>            <Typography variant="body1">
              Select which model statuses to include in the export:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {Object.keys(exportStatusFilters).sort().map(status => (
                <FormControlLabel
                  key={status}
                  control={
                    <Switch
                      checked={exportStatusFilters[status] || false}
                      onChange={(e) => handleStatusFilterChange(status, e.target.checked)}
                    />
                  }
                  label={`${status} (${models.filter(m => m.status === status).length} models)`}
                />
              ))}
            </Box>
            {Object.keys(exportStatusFilters).length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {Object.values(exportStatusFilters).filter(Boolean).length > 0 
                  ? `Will export ${models.filter(model => exportStatusFilters[model.status]).length} models`
                  : 'No models will be exported with current selection'
                }
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleExportConfirm}
            variant="contained"
            color="primary"
            disabled={Object.values(exportStatusFilters).filter(Boolean).length === 0}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import LLM Models</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body1">
              Import models from a JSON file. Ensure the file is in the correct format.
            </Typography>
            <TextField
              label="Paste JSON Data"
              multiline
              rows={10}
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              variant="outlined"
              fullWidth
              helperText="Or use the file upload option below"
            />
            <Button
              variant="contained"
              component="label"
              size="small"
            >
              Upload File
              <input
                type="file"
                accept=".json"
                onChange={handleImportFile}
                hidden
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            color="primary"
            disabled={importing}
          >
            {importing ? <CircularProgress size={24} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
